// ============================================================
// THE EFFECTIVE SHEET - a combatant's sheet as what is up leaves it.
//
// An Always on item, a Solo mode switched on, or a squad ability joined
// adds its effects to the character (plan modes-rework; "stances" before
// it). Rather than teach every resolver about them, the effects go on the
// sheet's own always-on list (`permanentEffects`) on a DERIVED sheet, and
// everything that resolves a character resolves that one.
//
// IDENTITY IS THE CONTRACT. Projections are memoised on the sheet object
// (`WeakMap`), so:
//
//   - with nothing up, the stored sheet itself is returned - nothing
//     downstream can tell such bundles exist;
//   - with some up, the SAME derived object is returned for the same
//     stored sheet and the same bundles up, however often it is asked. A new
//     object each call would miss every cache on every render.
//
// "Up" is a Solo mode switched on by hand (none in Squad Mode), or an
// Always on item - a TRIGGERED one ("while Prone") for exactly as long as
// the combatant carries the condition (`soloModes.upNow`).
//
// THE SHEET TIER (plan armoury, stage 3): some effects change the sheet
// itself - a characteristic, a talent or trait granted, wounds - and are
// applied HERE on the derived sheet; armour, resistance and Unnatural ride
// the always-on list the resolver already reads. Such an effect may wait on
// a condition about oneself ("Toughness +10 while wounded", `isSelfOnly`):
// it is judged here from the combatant's own state (`selfFactsOf`), and its
// verdict is part of the cache key, so identity still holds.
//
// The cache hangs off the stored sheet, so it is dropped when the sheet is
// edited (a sheet is never mutated in place - an edit is a new object).
//
// WHO MUST NOT USE THIS: anything that EDITS or DIFFS the sheet - pins,
// back-fill, sheet edits - reads `c.sheet`. A derived sheet written back
// would make what is up permanent.
//
// Pure. Zero React dependency.
// ============================================================
import { holds } from '../character/effectWhen';
import { selfFactsOf } from '../character/selfFacts';
import { isSheetKind, readEffects } from '../character/effectItems';
import { upEffects, upNow, type Up } from '../character/soloModes';
import type { StanceEffect } from '../character/effectItems';
import type { Character } from '../character/types';
import type { Combatant } from './types';

const derived = new WeakMap<Character, Map<string, Character>>();

/** The effects of what is up, each carrying the bundle it came from so whatever applies it can say so. */
const bundleEffects = (up: readonly Up[]): StanceEffect[] =>
  upEffects(up).map(({ effect, source }) => ({ ...effect, source }));

type Sheetly = Extract<StanceEffect, { type: 'characteristic_bonus' | 'grant_talent' | 'grant_trait' | 'wound_bonus' }>;

/** What a sheet-tier effect does to the sheet itself; the others ride `permanentEffects` to the resolver. */
const SHEET_CHANGES: { [K in Sheetly['type']]: (c: Character, e: Extract<Sheetly, { type: K }>) => Character } = {
  characteristic_bonus: (c, e) => ({
    ...c,
    characteristics: {
      ...c.characteristics,
      [e.characteristic]: {
        ...c.characteristics[e.characteristic],
        value: c.characteristics[e.characteristic].value + e.value,
      },
    },
  }),
  grant_talent: (c, e) => ({
    ...c,
    talents: [...c.talents, { talentId: e.talentId, ...(e.parameter ? { parameter: e.parameter } : {}) }],
  }),
  grant_trait: (c, e) => ({
    ...c,
    traits: [...c.traits, { traitId: e.traitId, ...(e.parameter ? { parameter: e.parameter } : {}) }],
  }),
  wound_bonus: (c, e) => ({ ...c, wounds: { ...c.wounds, max: c.wounds.max + e.value } }),
};

/** Strip the condition from an effect that holds now: the resolver applies only unconditional ones. */
const unconditioned = (e: StanceEffect): StanceEffect => {
  const { when: _was, ...rest } = e;
  return rest as StanceEffect;
};

/**
 * The derived sheet: the stances' effects on the always-on list, the sheet-tier conditions that hold made
 * unconditional, and every sheet-tier effect that holds applied to the sheet itself.
 */
function derive(sheet: Character, added: StanceEffect[], holdingNow: StanceEffect[]): Character {
  const permanentEffects = [
    ...(sheet.permanentEffects ?? []),
    ...added,
    ...holdingNow.filter((e) => e.when).map(unconditioned),
  ];
  let out: Character = { ...sheet, permanentEffects };
  for (const e of holdingNow) {
    const change = SHEET_CHANGES[e.type as Sheetly['type']] as
      ((c: Character, e: StanceEffect) => Character) | undefined;
    if (change) out = change(out, e);
  }
  return out;
}

/**
 * The sheet-tier effects, each with whether it holds: an unconditional one always, one with a condition about
 * oneself when that holds now. The verdicts, in order, are part of the cache key.
 */
function sheetVerdicts(c: Pick<Combatant, 'sheet' | 'state'>, pool: readonly StanceEffect[]) {
  const tier = pool.filter((e) => isSheetKind(e.type));
  const self = tier.some((e) => e.when) ? selfFactsOf(c.sheet, c.state) : null;
  return tier.map((effect) => ({ effect, holds: !effect.when || holds(effect.when, { self: self! }) === true }));
}

/** The effects of the squad abilities he joined (Squad Mode, 2026-10-23), named by them, and their part of the key. */
function joinedEffects(c: Pick<Combatant, 'state'>): { effects: StanceEffect[]; key: string } {
  const joined = c.state?.killTeam?.joined ?? [];
  return {
    effects: joined.flatMap((j) => j.effects.map((e) => ({ ...e, source: j.name }))),
    key: joined.map((j) => `${j.key}@${j.rev}`).join(','),
  };
}

/** The sheet every resolver should read for this combatant: stored, or wearing what is up and its state make it. */
export function effectiveSheet(c: Pick<Combatant, 'sheet' | 'state'>): Character {
  const { sheet } = c;
  // Always on and the Solo modes switched on (none in Squad Mode - D5); a trigger by the condition it waits on.
  const up = upNow(sheet, c.state);
  const active = up.map((u) => u.key);
  const joined = joinedEffects(c);
  const added = [...bundleEffects(up), ...joined.effects];
  const verdicts = sheetVerdicts(c, [...readEffects(sheet.permanentEffects), ...added]);
  const holdingNow = verdicts.filter((v) => v.holds).map((v) => v.effect);
  if (active.length === 0 && holdingNow.length === 0 && !joined.key) return sheet;
  // Which stances, which sheet effects hold, and which squad abilities: the same answer is the same object.
  const key = `${active.join('\u0000')}|${verdicts.map((v) => (v.holds ? 1 : 0)).join('')}|kt:${joined.key}`;
  let bySet = derived.get(sheet);
  if (!bySet) derived.set(sheet, (bySet = new Map()));
  let out = bySet.get(key);
  if (!out) bySet.set(key, (out = derive(sheet, added, holdingNow)));
  return out;
}
